import React, { useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(t("resetInvalidLink", "This reset link is invalid or incomplete."));
      return;
    }
    if (password.length < 8) {
      setError(t("resetPasswordTooShort", "Password must be at least 8 characters."));
      return;
    }
    if (password !== confirm) {
      setError(t("resetPasswordMismatch", "Passwords do not match."));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("resetPasswordSuccess", "Your password has been reset. Please sign in."));
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative bg-[#0B2545]">
        <img
          src="https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt={t("luxuryProperty", "Luxury property")}
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2545] via-[#0B2545]/60 to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-4">
            <img src="/panatlantik-logo.png" alt={t("panatlantikInternational", "PANATLANTIK INTERNATIONAL")} className="w-20 h-20 object-contain drop-shadow-lg" />
            <div>
              <div className="font-serif text-3xl tracking-tight">{t("panatlantikInternational", "PANATLANTIK INTERNATIONAL")}</div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mt-2">{t("realEstateCrm", "Real Estate CRM")}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8F9FA]">
        <div className="w-full max-w-md fade-up">
          <h2 className="font-serif text-3xl text-slate-900">{t("setNewPassword", "Set a new password")}</h2>
          <p className="text-slate-500 mt-2 mb-8">{t("enterNewPasswordBelow", "Enter a new password for your account below.")}</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="new-password">{t("newPassword", "New password")}</Label>
              <div className="relative mt-1.5">
                <Input
                  id="new-password"
                  type={show ? "text" : "password"}
                  data-testid="reset-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="rounded-sm pr-10 focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  aria-label={t("togglePassword", "Toggle password")}
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirm-password">{t("confirmPassword", "Confirm password")}</Label>
              <Input
                id="confirm-password"
                type={show ? "text" : "password"}
                data-testid="reset-password-confirm"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="mt-1.5 rounded-sm focus-visible:ring-1 focus-visible:ring-[#C5A059]"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="w-full rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white h-11 transition-colors"
            >
              {loading ? t("pleaseWait", "Please wait…") : t("resetPassword", "Reset password")}
            </Button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              className="text-sm text-[#C5A059] hover:underline w-full text-center"
              data-testid="reset-back-to-login"
            >
              {t("backToSignIn", "Back to sign in")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
